import Image from 'next/image'
import Link from 'next/link'
import * as React from 'react'

import { type NonFsdsTheme,resolveNonFsdsTheme } from '@/lib/non-fsds-theme'

import { TEMPLATE_DEFAULT_URLS } from '@/configs/template-default-urls'

interface NonFsdsHeroSectionProps {
  title: string
  description: string
  ctaStart: string
  ctaContinue: string
  ctaExplore: string
  ctaStartUrl?: string
  ctaContinueUrl?: string
  ctaExploreUrl?: string
  trustItems: string[]
  isCourseEnrolled: boolean
  isEnglish: boolean
  heroImage?: string
  theme?: NonFsdsTheme | string
}

export default function NonFsdsHeroSection({
  title,
  description,
  ctaStart,
  ctaContinue,
  ctaExplore,
  ctaStartUrl = TEMPLATE_DEFAULT_URLS.myCourses,
  ctaContinueUrl = TEMPLATE_DEFAULT_URLS.learningPath,
  ctaExploreUrl = TEMPLATE_DEFAULT_URLS.learningPath,
  trustItems,
  isCourseEnrolled,
  isEnglish,
  heroImage,
  theme = 'general',
}: NonFsdsHeroSectionProps) {
  const heroImageAlt = isEnglish ? 'Learners and mentors collaborating' : 'Học viên và mentor đang cộng tác'
  const collageBadges = isEnglish
    ? [
        { value: '20k+', label: 'learners joined' },
        { value: '4.8/5', label: 'mentor rating' },
      ]
    : [
        { value: '20k+', label: 'học viên tham gia' },
        { value: '4.8/5', label: 'đánh giá mentor' },
      ]

  const themeStyles = {
    general: {
      bg: 'from-blue-50 via-sky-50 to-white',
      border: 'border-blue-100',
      badge: 'from-blue-100 to-sky-100 text-blue-700',
      badgeIcon: 'text-blue-500',
      cardShadow: 'shadow-[0_8px_40px_-12px_rgba(37,99,235,0.12)]',
      ctaPrimary: 'from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-[0_4px_16px_-4px_rgba(37,99,235,0.5)] hover:shadow-[0_6px_20px_-4px_rgba(37,99,235,0.6)] focus-visible:ring-blue-600',
      ctaSecondary: 'border-blue-200 text-blue-700 hover:border-blue-300 hover:bg-blue-50 focus-visible:ring-blue-600',
      trustItem: 'border-blue-100 bg-blue-50/80',
      trustIcon: 'text-blue-500',
      imageFrame: 'border-blue-100/80 shadow-[0_12px_48px_-16px_rgba(37,99,235,0.15)]',
      imageOverlay: 'from-blue-900/20 to-blue-200/30',
      statValue: 'text-blue-700',
    },
    'english-academy': {
      bg: 'from-blue-50 via-emerald-50 to-white',
      border: 'border-blue-100',
      badge: 'from-blue-100 to-emerald-100 text-blue-700',
      badgeIcon: 'text-emerald-500',
      cardShadow: 'shadow-[0_8px_40px_-12px_rgba(37,99,235,0.12)]',
      ctaPrimary: 'from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 shadow-[0_4px_16px_-4px_rgba(37,99,235,0.5)] hover:shadow-[0_6px_20px_-4px_rgba(37,99,235,0.6)] focus-visible:ring-blue-600',
      ctaSecondary: 'border-emerald-200 text-blue-700 hover:border-emerald-300 hover:bg-emerald-50 focus-visible:ring-blue-600',
      trustItem: 'border-emerald-100 bg-emerald-50/80',
      trustIcon: 'text-emerald-500',
      imageFrame: 'border-blue-100/80 shadow-[0_12px_48px_-16px_rgba(37,99,235,0.15)]',
      imageOverlay: 'from-blue-900/20 to-emerald-200/30',
      statValue: 'text-blue-700',
    },
    programming: {
      bg: 'from-indigo-50 via-blue-50 to-white',
      border: 'border-indigo-100',
      badge: 'from-indigo-100 to-blue-100 text-indigo-700',
      badgeIcon: 'text-indigo-500',
      cardShadow: 'shadow-[0_8px_40px_-12px_rgba(99,102,241,0.12)]',
      ctaPrimary: 'from-indigo-600 to-cyan-600 hover:from-indigo-700 hover:to-cyan-700 shadow-[0_4px_16px_-4px_rgba(99,102,241,0.5)] hover:shadow-[0_6px_20px_-4px_rgba(99,102,241,0.6)] focus-visible:ring-indigo-600',
      ctaSecondary: 'border-indigo-200 text-indigo-700 hover:border-indigo-300 hover:bg-indigo-50 focus-visible:ring-indigo-600',
      trustItem: 'border-indigo-100 bg-indigo-50/80',
      trustIcon: 'text-indigo-500',
      imageFrame: 'border-indigo-100/80 shadow-[0_12px_48px_-16px_rgba(99,102,241,0.15)]',
      imageOverlay: 'from-indigo-900/20 to-cyan-200/30',
      statValue: 'text-indigo-700',
    },
    math: {
      bg: 'from-teal-50 via-sky-50 to-white',
      border: 'border-teal-100',
      badge: 'from-teal-100 to-sky-100 text-teal-700',
      badgeIcon: 'text-teal-500',
      cardShadow: 'shadow-[0_8px_40px_-12px_rgba(20,184,166,0.12)]',
      ctaPrimary: 'from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 shadow-[0_4px_16px_-4px_rgba(20,184,166,0.5)] hover:shadow-[0_6px_20px_-4px_rgba(20,184,166,0.6)] focus-visible:ring-teal-600',
      ctaSecondary: 'border-teal-200 text-teal-700 hover:border-teal-300 hover:bg-teal-50 focus-visible:ring-teal-600',
      trustItem: 'border-teal-100 bg-teal-50/80',
      trustIcon: 'text-teal-500',
      imageFrame: 'border-teal-100/80 shadow-[0_12px_48px_-16px_rgba(20,184,166,0.15)]',
      imageOverlay: 'from-teal-900/20 to-sky-200/30',
      statValue: 'text-teal-700',
    },
  }[resolveNonFsdsTheme(theme)]

  return (
    <section
      data-testid="non-fsds-hero-section"
      className={`relative overflow-hidden border-b ${themeStyles.border} bg-gradient-to-br px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24 ${themeStyles.bg}`}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.14),transparent_48%)]" />
      <div className="pointer-events-none absolute right-0 top-0 h-96 w-96 bg-[radial-gradient(circle_at_center,rgba(14,165,233,0.08),transparent_60%)]" />
      <div className="pointer-events-none absolute bottom-0 left-1/2 h-64 w-[600px] -translate-x-1/2 bg-[radial-gradient(ellipse_at_center,rgba(59,130,246,0.06),transparent_70%)]" />

      <div className="relative mx-auto grid w-full max-w-7xl items-center gap-10 lg:grid-cols-[minmax(0,1.06fr)_minmax(0,0.94fr)] lg:gap-14">
        <div className={`rounded-[28px] border border-slate-100/80 bg-white/90 px-6 py-8 ${themeStyles.cardShadow} backdrop-blur-sm sm:px-8 sm:py-10 lg:px-10 lg:py-12`}>
          <p className={`mb-5 inline-flex items-center gap-2 rounded-full bg-gradient-to-r px-4 py-1.5 text-sm font-semibold shadow-sm ${themeStyles.badge}`}>
            <svg
              className={`size-4 ${themeStyles.badgeIcon}`}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5"
              />
            </svg>
            {isEnglish ? 'Leadership learning community' : 'Cộng đồng học tập định hướng lãnh đạo'}
          </p>
          <h1 className="max-w-3xl text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-[2.75rem] lg:leading-[1.15]">
            {title}
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">{description}</p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Link
              href={isCourseEnrolled ? ctaContinueUrl : ctaStartUrl}
              className={`group inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r px-7 py-3 text-base font-semibold text-white transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 sm:w-auto ${themeStyles.ctaPrimary}`}
            >
              {isCourseEnrolled ? ctaContinue : ctaStart}
              <svg
                className="size-4 transition-transform duration-200 group-hover:translate-x-0.5"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </Link>
            <Link
              href={ctaExploreUrl}
              className={`inline-flex min-h-12 w-full items-center justify-center rounded-full border bg-white px-7 py-3 text-base font-semibold shadow-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 sm:w-auto ${themeStyles.ctaSecondary}`}
            >
              {ctaExplore}
            </Link>
          </div>

          <div className="mt-8 flex flex-wrap gap-2.5">
            {trustItems.map(item => (
              <span
                key={item}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium text-slate-700 ${themeStyles.trustItem}`}
              >
                <svg className={`size-3.5 ${themeStyles.trustIcon}`} fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z"
                    clipRule="evenodd"
                  />
                </svg>
                {item}
              </span>
            ))}
          </div>
        </div>

        {/* Right: Image collage */}
        <div className={`relative overflow-hidden rounded-[28px] border bg-white/80 p-3 sm:p-4 ${themeStyles.imageFrame}`}>
          <div className="relative overflow-hidden rounded-2xl">
            <Image
              src={heroImage || '/images/laptop.jpg'}
              alt={heroImageAlt}
              width={1280}
              height={720}
              priority
              className="h-full min-h-[320px] w-full object-cover object-center"
            />
            <div className={`pointer-events-none absolute inset-0 bg-gradient-to-tr via-transparent ${themeStyles.imageOverlay}`} />

            <div className="absolute inset-x-3 bottom-3 flex items-end justify-between gap-2 sm:inset-x-4 sm:bottom-4 sm:gap-3">
              {collageBadges.map((badge, index) => (
                <div
                  key={badge.label}
                  className={`max-w-[calc(50%-0.25rem)] rounded-xl border border-white/70 bg-white/95 px-3.5 py-2.5 shadow-lg backdrop-blur-md sm:max-w-none sm:px-5 sm:py-3 ${
                    index === 1 ? 'text-right' : ''
                  }`}
                >
                  <p className={`text-base font-bold sm:text-lg ${themeStyles.statValue}`}>{badge.value}</p>
                  <p className="text-xs font-medium text-slate-600 sm:text-sm">{badge.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
